import { useSetAtom } from "jotai";
import type { JSX } from "react";

import {
  type AnyRoute,
  Outlet,
  createRoute,
  useNavigate,
} from "@tanstack/react-router";

import { ProjectEditorDialog } from "../ProjectEditorDialog";
import { ProjectList } from "../ProjectList";
import { reloadProjectListAtom } from "../stores/list";
import { refreshProjectQuickviewAtom } from "../stores/quickview";
import type { ProjectType } from "../types";

const DRAFT = "new";

type Navigate = (options: {
  to: string;
  params?: Record<string, unknown>;
  search?: Record<string, unknown>;
  replace?: boolean;
}) => Promise<void>;

export type ProjectRoutesOptions = {
  type?: ProjectType | "all";
};

export function projectRoutes(
  parent: AnyRoute,
  { type = "all" }: ProjectRoutesOptions = {},
): AnyRoute {
  const fallback: ProjectType = "all" === type ? "subscription" : type;

  const section = createRoute({
    getParentRoute: () => parent,
    id: "projects",
    component: function ProjectSectionRoute(): JSX.Element {
      const navigate = useNavigate() as unknown as Navigate;

      return (
        <>
          <ProjectList
            type={type}
            onOpen={({ projectId, type: opened }) =>
              void navigate({
                to: editor.to,
                params: { projectId },
                search: null === projectId ? { type: opened } : {},
              })
            }
          />

          <Outlet />
        </>
      );
    },
  });

  const index = createRoute({
    getParentRoute: () => section,
    path: "/",
    component: () => null,
  });

  const editor = createRoute({
    getParentRoute: () => section,
    path: "$projectId",
    params: {
      parse: ({ projectId }) => ({
        projectId: DRAFT === projectId ? null : Number(projectId),
      }),
      stringify: ({ projectId }) => ({
        projectId: null === projectId ? DRAFT : String(projectId),
      }),
    },
    validateSearch: (
      search: Record<string, unknown>,
    ): { type?: ProjectType; subscription?: number } => {
      const kind: { type?: ProjectType } =
        "intervention" === search.type || "subscription" === search.type
          ? { type: search.type }
          : {};

      const parent = Number(search.subscription);

      return Number.isInteger(parent) && 0 < parent
        ? { ...kind, subscription: parent }
        : kind;
    },
    component: function ProjectEditorRoute(): JSX.Element {
      const { projectId } = editor.useParams();
      const { type: draft, subscription } = editor.useSearch();
      const navigate = useNavigate() as unknown as Navigate;

      const reload = useSetAtom(reloadProjectListAtom);
      const refreshQuickview = useSetAtom(refreshProjectQuickviewAtom);

      const close = () => void navigate({ to: index.to, replace: true });

      return (
        <ProjectEditorDialog
          key={
            projectId ?? `${DRAFT}-${draft ?? fallback}-${subscription ?? ""}`
          }
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          projectId={projectId}
          type={draft ?? fallback}
          subscriptionId={null === projectId ? (subscription ?? null) : null}
          onSaved={() => {
            close();

            void refreshQuickview();
            void reload();
          }}
          onCancel={close}
        />
      );
    },
  });

  return section.addChildren([index, editor]);
}
